/**
 * Macro #26 · Voxel 3D — PURE client geometry/accounting (Increment 2).
 *
 * Deterministic, integer-only. Mirrors the CERTIFIED Increment-1 backend law
 * (`backend/app/territory3d/voxel.py`) for UX pre-flight ONLY. The backend
 * remains the FINAL authority for capacity / occupancy / collision / XY / Z /
 * revision / ownership. These helpers never invent authority — they let the
 * editor give immediate feedback and the renderer place voxels deterministically.
 *
 * Frozen laws reflected here:
 *  - filled-voxel accounting (never the allocated bounding volume)
 *  - z ∈ [0, 30): origins 0..29, top ≤ 30 (no off-by-one)
 *  - exclusive voxel occupancy (collision)
 *  - voxel-safe 90° rotations about Z (integer grid preserved)
 *  - voxels must stay inside the real owned XY footprint
 */
export const Z_MAX_FREE = 30;
export const ROTATIONS = [0, 90, 180, 270] as const;
export type Rotation = (typeof ROTATIONS)[number];

export type Voxel = [number, number, number, string]; // [lx, ly, lz, color]

export interface WorkArt {
  work_art_id: string;
  allowed_volume: { w: number; h: number; d: number };
  placement: { x: number; y: number; z: number };
  rotation: number;
  voxels: Voxel[];
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WorldVoxel {
  x: number;
  y: number;
  z: number;
  color: string;
}

export interface CapacitySummary {
  used_voxels: number;
  capacity_voxels: number;
  available_voxels: number;
  over_capacity: boolean;
}

export function isRotation(deg: number): deg is Rotation {
  return deg === 0 || deg === 90 || deg === 180 || deg === 270;
}

/** Voxel-safe 90° rotation of a local (lx,ly) inside a w×h footprint about Z.
 * Integer-grid preserving and bijective. For 90/270 the footprint dims swap. */
export function rotateXY(lx: number, ly: number, w: number, h: number, deg: number): [number, number] {
  switch (deg) {
    case 0:
      return [lx, ly];
    case 90:
      return [h - 1 - ly, lx];
    case 180:
      return [w - 1 - lx, h - 1 - ly];
    case 270:
      return [ly, w - 1 - lx];
    default:
      throw new Error(`rotation ${deg} is not a voxel-safe 90° step`);
  }
}

/** Exact set of integer ground cells across owned parcel rects, keyed "x,y". */
export function ownedCells(rects: readonly Rect[]): Set<string> {
  const cells = new Set<string>();
  for (const r of rects) {
    for (let cx = r.x; cx < r.x + r.w; cx++) {
      for (let cy = r.y; cy < r.y + r.h; cy++) {
        cells.add(`${cx},${cy}`);
      }
    }
  }
  return cells;
}

export type SceneErrorCode =
  | "VOXEL_VOLUME_INVALID"
  | "VOXEL_Z_INVALID"
  | "VOXEL_OUT_OF_VOLUME"
  | "VOXEL_Z_EXCEEDED"
  | "VOXEL_OUT_OF_BOUNDS"
  | "VOXEL_COLLISION"
  | "VOXEL_ROTATION_INVALID";

/** Thrown by the strict expander; carries the SAME stable code the backend
 * authority emits so the editor/renderer can surface it identically. */
export class VoxelPreflightError extends Error {
  readonly code: SceneErrorCode;
  readonly detail?: string;
  constructor(code: SceneErrorCode, detail?: string) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = "VoxelPreflightError";
    this.code = code;
    this.detail = detail;
  }
}

/** Expand ONE work art's FILLED voxels to world coordinates (applies rotation +
 * placement). STRICT — mirrors the certified backend `expand_work_art`: rejects
 * a non-positive allowed_volume, a non-90° rotation, a negative placement.z and
 * any voxel outside its declared local volume (never silently drops voxels, so
 * the client "used" count can never disagree with the authority). */
export function expandWorkArt(wa: WorkArt): WorldVoxel[] {
  const { w, h, d } = wa.allowed_volume;
  if (w <= 0 || h <= 0 || d <= 0) {
    throw new VoxelPreflightError("VOXEL_VOLUME_INVALID", `w=${w} h=${h} d=${d}`);
  }
  const deg = wa.rotation ?? 0;
  if (!isRotation(deg)) {
    throw new VoxelPreflightError("VOXEL_ROTATION_INVALID", `rotation ${deg}`);
  }
  const { x: px, y: py, z: pz } = wa.placement;
  if (pz < 0) {
    throw new VoxelPreflightError("VOXEL_Z_INVALID", `placement.z=${pz}`);
  }
  const out: WorldVoxel[] = [];
  for (const v of wa.voxels) {
    const [lx, ly, lz, color] = v;
    if (!(lx >= 0 && lx < w && ly >= 0 && ly < h && lz >= 0 && lz < d)) {
      throw new VoxelPreflightError("VOXEL_OUT_OF_VOLUME", `(${lx},${ly},${lz}) outside ${w}x${h}x${d}`);
    }
    const [rx, ry] = rotateXY(lx, ly, w, h, deg);
    out.push({ x: px + rx, y: py + ry, z: pz + lz, color: String(color) });
  }
  return out;
}

/** Total FILLED voxels across all work arts (never allocated volume). */
export function filledVoxelCount(workArts: readonly WorkArt[]): number {
  let n = 0;
  for (const wa of workArts) n += wa.voxels.length;
  return n;
}

/** One projected ground cell in WORLD coordinates (same system as parcels). */
export interface TopDownCell {
  x: number;
  y: number;
  color: string;
}

/** Top-down (bird's-eye) projection of a published 3D scene: for every occupied
 * world (x,y) column, the colour of the HIGHEST voxel (max z) — i.e. what you
 * would see looking straight down onto the model. World coordinates match the
 * owned parcel footprint (placement = footprint.x/y + local), so the returned
 * cells align 1:1 with the territory on the Grid. Fail-soft: a work art that
 * fails strict expansion is skipped so a public render never throws. */
export function topDownProjection(workArts: readonly WorkArt[]): TopDownCell[] {
  const top = new Map<string, { z: number; color: string }>();
  for (const wa of workArts) {
    let world: WorldVoxel[];
    try {
      world = expandWorkArt(wa);
    } catch {
      continue;
    }
    for (const v of world) {
      const key = `${v.x},${v.y}`;
      const cur = top.get(key);
      if (!cur || v.z > cur.z) top.set(key, { z: v.z, color: v.color });
    }
  }
  const out: TopDownCell[] = [];
  for (const [key, val] of top) {
    const comma = key.indexOf(",");
    out.push({ x: Number(key.slice(0, comma)), y: Number(key.slice(comma + 1)), color: val.color });
  }
  return out;
}

/** World XY bounding box of a scene's FILLED voxels (fail-soft: invalid work
 * arts are skipped). Null for an empty/all-invalid scene. */
export function sceneBoundsXY(
  workArts: readonly WorkArt[],
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, any = false;
  for (const wa of workArts) {
    let world: WorldVoxel[];
    try { world = expandWorkArt(wa); } catch { continue; }
    for (const v of world) {
      any = true;
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }
  }
  return any ? { minX, minY, maxX, maxY } : null;
}

/** Rigidly translate the WHOLE scene by integer world cells (moves every work
 * art's placement). Rotation/voxels are untouched, so occupancy/collision/Z are
 * invariant — only XY changes, which the caller validates against owned cells. */
export function translateScene(workArts: readonly WorkArt[], dx: number, dy: number): WorkArt[] {
  return workArts.map((wa) => ({
    ...wa,
    placement: { ...wa.placement, x: wa.placement.x + dx, y: wa.placement.y + dy },
  }));
}

/** Rotate the WHOLE scene 90° clockwise about its own centre, preserving each
 * work art as its own unit. The rotation is BAKED into fresh local voxel
 * coordinates (rotation reset to 0) so the result is a plain, valid scene the
 * backend authority accepts. Returns null when the scene can't be expanded; the
 * caller still validates the result against the owned footprint (a rotation that
 * would leave the parcels is rejected upstream). */
export function rotateScene90(workArts: readonly WorkArt[]): WorkArt[] | null {
  const groups: { wa: WorkArt; world: WorldVoxel[] }[] = [];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, any = false;
  for (const wa of workArts) {
    let world: WorldVoxel[];
    try { world = expandWorkArt(wa); } catch { return null; }
    groups.push({ wa, world });
    for (const v of world) {
      any = true;
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }
  }
  if (!any) return null;
  const Sh = maxY - minY + 1;
  // 90° CW inside the scene box (matches the certified rotateXY law), anchored
  // at (minX,minY): local (lx,ly)->(Sh-1-ly, lx).
  const rot = (v: WorldVoxel) => ({
    x: minX + (Sh - 1 - (v.y - minY)),
    y: minY + (v.x - minX),
    z: v.z,
    color: v.color,
  });
  let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
  for (const g of groups) for (const v of g.world) {
    const r = rot(v);
    if (r.x < rMinX) rMinX = r.x;
    if (r.y < rMinY) rMinY = r.y;
    if (r.x > rMaxX) rMaxX = r.x;
    if (r.y > rMaxY) rMaxY = r.y;
  }
  // Re-centre so the piece rotates in place rather than drifting to a corner.
  const offX = Math.round((minX + maxX) / 2 - (rMinX + rMaxX) / 2);
  const offY = Math.round((minY + maxY) / 2 - (rMinY + rMaxY) / 2);
  const out: WorkArt[] = [];
  for (const g of groups) {
    const rv = g.world.map((v) => { const r = rot(v); return { x: r.x + offX, y: r.y + offY, z: r.z, color: r.color }; });
    let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
    for (const v of rv) {
      if (v.x < bx0) bx0 = v.x;
      if (v.y < by0) by0 = v.y;
      if (v.x > bx1) bx1 = v.x;
      if (v.y > by1) by1 = v.y;
    }
    out.push({
      work_art_id: g.wa.work_art_id,
      allowed_volume: { w: bx1 - bx0 + 1, h: by1 - by0 + 1, d: Z_MAX_FREE },
      placement: { x: bx0, y: by0, z: 0 },
      rotation: 0,
      voxels: rv.map((v) => [v.x - bx0, v.y - by0, v.z, v.color] as Voxel),
    });
  }
  return out;
}

export interface ScenePreflight {
  ok: boolean;
  used: number;
  world: WorldVoxel[];
  error?: SceneErrorCode;
  detail?: string;
}

/** Non-throwing scene validation for UX pre-flight. Mirrors the certified
 * backend `validate_scene`: strict per-work-art expansion (volume / rotation /
 * placement.z / local out-of-volume) then world-level z∈[0,30), XY inside the
 * owned footprint, and EXCLUSIVE occupancy. Backend remains authoritative. */
export function validateScene(workArts: readonly WorkArt[], cells: Set<string>): ScenePreflight {
  const occupied = new Set<string>();
  const world: WorldVoxel[] = [];
  let used = 0;
  for (const wa of workArts) {
    let expanded: WorldVoxel[];
    try {
      expanded = expandWorkArt(wa);
    } catch (e) {
      if (e instanceof VoxelPreflightError) {
        return { ok: false, used, world, error: e.code, detail: e.detail };
      }
      throw e;
    }
    for (const v of expanded) {
      if (!(v.z >= 0 && v.z < Z_MAX_FREE)) {
        return { ok: false, used, world, error: "VOXEL_Z_EXCEEDED", detail: `z=${v.z}` };
      }
      if (!cells.has(`${v.x},${v.y}`)) {
        return { ok: false, used, world, error: "VOXEL_OUT_OF_BOUNDS", detail: `(${v.x},${v.y})` };
      }
      const key = `${v.x},${v.y},${v.z}`;
      if (occupied.has(key)) {
        return { ok: false, used, world, error: "VOXEL_COLLISION", detail: key };
      }
      occupied.add(key);
      world.push(v);
      used += 1;
    }
  }
  return { ok: true, used, world };
}

export function capacitySummary(used: number, capacity: number): CapacitySummary {
  return {
    used_voxels: used,
    capacity_voxels: capacity,
    available_voxels: Math.max(0, capacity - used),
    over_capacity: used > capacity,
  };
}

/** Deterministic, order-independent canonical form (semantic equality — matches
 * the backend grandfather comparison). */
export function canonicalScene(workArts: readonly WorkArt[]): string {
  const norm = workArts.map((wa) => ({
    work_art_id: String(wa.work_art_id),
    allowed_volume: { w: wa.allowed_volume.w, h: wa.allowed_volume.h, d: wa.allowed_volume.d },
    placement: { x: wa.placement.x, y: wa.placement.y, z: wa.placement.z },
    rotation: wa.rotation ?? 0,
    voxels: [...wa.voxels]
      .map((v) => [v[0], v[1], v[2], String(v[3])] as Voxel)
      .sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2] || (a[3] < b[3] ? -1 : a[3] > b[3] ? 1 : 0)),
  }));
  norm.sort((a, b) => (a.work_art_id < b.work_art_id ? -1 : a.work_art_id > b.work_art_id ? 1 : 0));
  return JSON.stringify(norm);
}

/** Bounding box (world) of all owned cells — used to centre the shared scene. */
export function footprintBounds(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.w));
  const maxY = Math.max(...rects.map((r) => r.y + r.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
